import { makeAutoObservable, observable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { changedFields } from "@/lib/changed-fields";
import { pollExportStatus } from "@/components/data-table/use-csv-export";
import type {
  IProjectTemplatesListQuery,
  TPickableProjectTemplate,
} from "@/services/project-template.service";
import type { RootStore } from "./root.store";
import type {
  TCreateProjectFromTemplateOverrides,
  TCreatedProjectFromTemplate,
  TCreateProjectTemplatePayload,
  TProjectTemplateDetail,
  TProjectTemplateListItem,
  TTemplateCustomFieldEntry,
  TUpdateProjectTemplatePayload,
  TWorkitemTemplate,
} from "@/types/project-template";

export class ProjectTemplatesStore {
  // Immutable response rows: async form-prefill consumers read plain values.
  pickableByWorkspace = observable.map<string, TPickableProjectTemplate[]>(undefined, {
    deep: false,
  });
  pickableLoading = new Set<string>();
  private inflightPickable = new Map<string, Promise<TPickableProjectTemplate[]>>();
  templateListByWorkspace: Map<string, TProjectTemplateListItem[]> = new Map();
  templateDetailById: Map<string, TProjectTemplateDetail> = new Map();
  isLoadingList = false;
  isLoadingDetail = false;
  // Last template-list-load failure, surfaced to the DataTable `error` prop.
  // Cleared when a (re)fetch starts; set when fetchTemplates rejects.
  loadError: Error | null = null;

  // --- Paginated settings-list cache (parallel to templateListByWorkspace) ---
  // The settings Templates table is server-driven (filters/sort/pagination); the
  // full list above stays the READ bank for settings and custom-field filters.
  // Project creation uses the separate USE bank.
  pagedTemplates: TProjectTemplateListItem[] = [];
  pageInfo: { totalCount: number; hasNext: boolean; nextCursor: string | null } = {
    totalCount: 0,
    hasNext: false,
    nextCursor: null,
  };
  isPageLoading = false;
  isLoadingMore = false;
  pageError: Error | null = null;
  private lastPageQuery: IProjectTemplatesListQuery = {};

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(
      this,
      { rootStore: false, lastPageQuery: false, inflightPickable: false } as never,
      {
        autoBind: true,
      },
    );
  }

  templatesForSlug(workspaceSlug: string): TProjectTemplateListItem[] {
    return this.templateListByWorkspace.get(workspaceSlug) ?? [];
  }

  pickableForSlug(workspaceSlug: string): TPickableProjectTemplate[] {
    return this.pickableByWorkspace.get(workspaceSlug) ?? [];
  }

  async fetchPickableTemplates(workspaceSlug: string): Promise<TPickableProjectTemplate[]> {
    const inflight = this.inflightPickable.get(workspaceSlug);
    if (inflight) return inflight;
    this.pickableLoading.add(workspaceSlug);
    const request = this.rootStore.projectTemplateService
      .listPickableTemplates(workspaceSlug)
      .then((rows) => {
        runInAction(() => this.pickableByWorkspace.set(workspaceSlug, rows));
        return rows;
      })
      .catch((error: unknown) => {
        console.error("Failed to load pickable templates", { workspaceSlug, error });
        throw error;
      })
      .finally(() => {
        runInAction(() => this.pickableLoading.delete(workspaceSlug));
        this.inflightPickable.delete(workspaceSlug);
      });
    this.inflightPickable.set(workspaceSlug, request);
    return request;
  }

  async fetchPickableTemplate(
    workspaceSlug: string,
    id: string,
  ): Promise<TPickableProjectTemplate> {
    const rows =
      this.pickableByWorkspace.get(workspaceSlug) ??
      (await this.fetchPickableTemplates(workspaceSlug));
    const template = rows.find((row) => row.id === id);
    if (!template) throw new Error("Template is not available to use");
    return template;
  }

  templateById(id: string): TProjectTemplateDetail | undefined {
    return this.templateDetailById.get(id);
  }

  async fetchTemplates(workspaceSlug: string): Promise<TProjectTemplateListItem[]> {
    this.isLoadingList = true;
    this.loadError = null;
    try {
      const list = await this.rootStore.projectTemplateService.listTemplates(workspaceSlug);
      runInAction(() => {
        this.templateListByWorkspace.set(workspaceSlug, list);
      });
      return list;
    } catch (err) {
      runInAction(() => {
        this.loadError = err instanceof Error ? err : new Error(String(err));
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoadingList = false;
      });
    }
  }

  async fetchTemplate(workspaceSlug: string, id: string): Promise<TProjectTemplateDetail> {
    this.isLoadingDetail = true;
    try {
      const detail = await this.rootStore.projectTemplateService.getTemplate(workspaceSlug, id);
      runInAction(() => {
        this.templateDetailById.set(detail.id, detail);
      });
      return detail;
    } finally {
      runInAction(() => {
        this.isLoadingDetail = false;
      });
    }
  }

  /** Load the first page of the settings list with the given server query
   *  (search / privacy / sort). Replaces the current page. */
  async loadTemplatesPage(
    workspaceSlug: string,
    query: IProjectTemplatesListQuery = {},
  ): Promise<void> {
    this.isPageLoading = true;
    this.pageError = null;
    this.lastPageQuery = query;
    try {
      const page = await this.rootStore.projectTemplateService.listTemplatesPage(workspaceSlug, {
        ...query,
        cursor: null,
      });
      runInAction(() => {
        this.pagedTemplates = page.results;
        this.pageInfo = {
          totalCount: page.total_count,
          hasNext: page.next_page_results,
          nextCursor: page.next_cursor || null,
        };
      });
    } catch (err) {
      runInAction(() => {
        this.pageError = err instanceof Error ? err : new Error(String(err));
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isPageLoading = false;
      });
    }
  }

  /** Append the next page (DataTable infinite scroll). Re-runs the first page
   *  when nothing is loaded yet (the error-empty "Try again" path). */
  async loadMoreTemplates(workspaceSlug: string): Promise<void> {
    if (this.pagedTemplates.length === 0) {
      await this.loadTemplatesPage(workspaceSlug, this.lastPageQuery);
      return;
    }
    if (!this.pageInfo.hasNext || !this.pageInfo.nextCursor || this.isLoadingMore) return;
    this.isLoadingMore = true;
    this.pageError = null;
    try {
      const page = await this.rootStore.projectTemplateService.listTemplatesPage(workspaceSlug, {
        ...this.lastPageQuery,
        cursor: this.pageInfo.nextCursor,
      });
      runInAction(() => {
        const seen = new Set(this.pagedTemplates.map((tpl) => tpl.id));
        this.pagedTemplates = [
          ...this.pagedTemplates,
          ...page.results.filter((tpl) => !seen.has(tpl.id)),
        ];
        this.pageInfo = {
          totalCount: page.total_count,
          hasNext: page.next_page_results,
          nextCursor: page.next_cursor || null,
        };
      });
    } catch (err) {
      runInAction(() => {
        this.pageError = err instanceof Error ? err : new Error(String(err));
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoadingMore = false;
      });
    }
  }

  /** Export the templates list as CSV, scoped to the current page query and
   *  (when `ids` is non-empty) restricted to the selected rows. */
  exportTemplatesCsv(workspaceSlug: string, ids?: string[], columns?: string[]): Promise<Response> {
    const body: { ids?: string[]; columns?: string[] } = {};
    if (ids && ids.length > 0) body.ids = ids;
    if (columns && columns.length > 0) body.columns = columns;
    return this.rootStore.projectTemplateService.exportTemplates(workspaceSlug, body, {
      q: this.lastPageQuery.q,
      privacy: this.lastPageQuery.privacy,
      order_by: this.lastPageQuery.order_by,
    });
  }

  pollExport(workspaceSlug: string, token: string): Promise<string | null> {
    return pollExportStatus(token, (tok) =>
      this.rootStore.projectTemplateService.getExportStatus(workspaceSlug, tok),
    );
  }

  /**
   * Project a detail response onto a list row.
   *
   * Team Access (#2926) echoes the server's teams so a scoping change shows on
   * the list without a refetch. `can_edit` is deliberately `null`, NOT copied
   * from the detail response: the detail value answers "can I edit THIS one",
   * and pasting it onto a list row would assert it for a row the API never
   * evaluated. Only the `?team=` list path carries a real per-row `can_edit`,
   * and that one arrives from the server.
   */
  private toListRow(detail: TProjectTemplateDetail): TProjectTemplateListItem {
    return {
      id: detail.id,
      template_name: detail.template_name,
      template_description: detail.template_description,
      cover_image: detail.cover_image,
      privacy: detail.privacy,
      member_count: detail.members?.length ?? 0,
      team_access: detail.team_access ?? [],
      can_edit: null,
      created_at: detail.created_at,
      updated_at: detail.updated_at,
    };
  }

  async createTemplate(
    workspaceSlug: string,
    data: TCreateProjectTemplatePayload,
    source: AnalyticsSource = "page",
  ): Promise<TProjectTemplateDetail> {
    const detail = await this.rootStore.projectTemplateService.createTemplate(workspaceSlug, data);
    runInAction(() => {
      this.templateDetailById.set(detail.id, detail);
      const existing = this.templateListByWorkspace.get(workspaceSlug) ?? [];
      this.templateListByWorkspace.set(workspaceSlug, [this.toListRow(detail), ...existing]);
    });
    track("template_created", { scope: "project", template_id: detail.id, source });
    return detail;
  }

  /**
   * Duplicate a template and show the copy immediately (#2926).
   *
   * The copy lands at the top of both caches regardless of the current sort or
   * search: it is the direct result of the user's action, and dropping it
   * because it doesn't match the active query would read as the action having
   * failed. The next page load re-sorts it into place.
   *
   * `teamId` scopes the copy to the team tab it was made from (GH-4093); absent
   * from workspace Settings, where the copy stays workspace-wide.
   */
  async duplicateTemplate(
    workspaceSlug: string,
    id: string,
    teamId?: string,
    source: AnalyticsSource = "page",
  ): Promise<TProjectTemplateDetail> {
    const detail = await this.rootStore.projectTemplateService.duplicateTemplate(
      workspaceSlug,
      id,
      teamId,
    );
    runInAction(() => {
      this.templateDetailById.set(detail.id, detail);
      const row = this.toListRow(detail);
      const existing = this.templateListByWorkspace.get(workspaceSlug);
      if (existing) {
        this.templateListByWorkspace.set(workspaceSlug, [row, ...existing]);
      }
      this.pagedTemplates = [row, ...this.pagedTemplates];
      this.pageInfo = { ...this.pageInfo, totalCount: this.pageInfo.totalCount + 1 };
    });
    // A copy is a new template, so it keeps template_created (renaming would split
    // the event's history) and names the template it came from.
    track("template_created", {
      scope: "project",
      template_id: detail.id,
      duplicated_from_id: id,
      source,
    });
    return detail;
  }

  async updateTemplate(
    workspaceSlug: string,
    id: string,
    data: TUpdateProjectTemplatePayload,
    source: AnalyticsSource = "page",
  ): Promise<TProjectTemplateDetail> {
    // Read before the write replaces it: `fields` is what this save changed. The
    // detail tabs send their whole form, so the body's keys alone would name
    // every field on every save. A template that isn't loaded counts every key.
    const fields = changedFields(data, this.templateDetailById.get(id));
    const detail = await this.rootStore.projectTemplateService.updateTemplate(
      workspaceSlug,
      id,
      data,
    );
    runInAction(() => {
      this.templateDetailById.set(detail.id, detail);
      const patched = this.toListRow(detail);
      const apply = (item: TProjectTemplateListItem) =>
        // Keep the row's server-computed `can_edit` — on the `?team=` list it is
        // a real verdict, and `toListRow`'s `null` would silently unlock a
        // view-only row after an unrelated edit elsewhere.
        item.id === detail.id ? { ...patched, can_edit: item.can_edit } : item;
      const existing = this.templateListByWorkspace.get(workspaceSlug);
      if (existing) {
        this.templateListByWorkspace.set(workspaceSlug, existing.map(apply));
      }
      // Keep the server-driven settings table's row in sync (inline name /
      // privacy edits) without a refetch.
      this.pagedTemplates = this.pagedTemplates.map(apply);
    });
    track("template_updated", { scope: "project", template_id: id, fields, source });
    return detail;
  }

  async updateWorkitemCustomFields(
    workspaceSlug: string,
    templateId: string,
    workitemId: string,
    customFields: TTemplateCustomFieldEntry[],
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const response = await this.rootStore.projectTemplateService.updateWorkitemTemplateCustomFields(
      workspaceSlug,
      templateId,
      workitemId,
      customFields,
    );
    // Patch the cached detail in place so the editor sees fresh data without
    // a full re-fetch. Walks roots + sub_workitems since the BE accepts both.
    runInAction(() => {
      const detail = this.templateDetailById.get(templateId);
      if (!detail) return;
      const patch = (item: TWorkitemTemplate): TWorkitemTemplate => {
        if (item.id === workitemId) {
          return {
            ...item,
            custom_fields: response.custom_fields,
            custom_fields_schema: response.custom_fields_schema,
          };
        }
        if (item.sub_workitems && item.sub_workitems.length > 0) {
          return { ...item, sub_workitems: item.sub_workitems.map(patch) };
        }
        return item;
      };
      const next: TProjectTemplateDetail = {
        ...detail,
        workitems: detail.workitems.map(patch),
      };
      this.templateDetailById.set(templateId, next);
    });
    track("template_updated", {
      scope: "project",
      template_id: templateId,
      work_item_template_id: workitemId,
      fields: ["work_item_custom_fields"],
      source,
    });
  }

  async createFromTemplate(
    workspaceSlug: string,
    templateId: string,
    overrides: TCreateProjectFromTemplateOverrides,
    source: AnalyticsSource = "page",
  ): Promise<TCreatedProjectFromTemplate> {
    const created = await this.rootStore.projectTemplateService.createProjectFromTemplate(
      workspaceSlug,
      templateId,
      overrides,
    );
    this.rootStore.project.hydrateProjectDetail(workspaceSlug, created);
    track("project_created_from_template", {
      project_id: created.id,
      template_id: templateId,
      source,
    });
    return created;
  }

  async deleteTemplate(
    workspaceSlug: string,
    id: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    await this.removeTemplate(workspaceSlug, id);
    track("template_deleted", { scope: "project", template_id: id, source });
  }

  /**
   * Delete a selection one by one, stopping at the first failure. One
   * `template_deleted` for the whole action, counting the templates that were
   * deleted, including when a later one fails.
   */
  async bulkDeleteTemplates(workspaceSlug: string, ids: readonly string[]): Promise<void> {
    let count = 0;
    try {
      for (const id of ids) {
        await this.removeTemplate(workspaceSlug, id);
        count += 1;
      }
    } finally {
      if (count > 0) track("template_deleted", { scope: "project", count, source: "bulk" });
    }
  }

  private async removeTemplate(workspaceSlug: string, id: string): Promise<void> {
    await this.rootStore.projectTemplateService.deleteTemplate(workspaceSlug, id);
    runInAction(() => {
      this.templateDetailById.delete(id);
      const existing = this.templateListByWorkspace.get(workspaceSlug);
      if (existing) {
        this.templateListByWorkspace.set(
          workspaceSlug,
          existing.filter((item) => item.id !== id),
        );
      }
      // Keep the server-driven settings table in sync without a refetch.
      const before = this.pagedTemplates.length;
      this.pagedTemplates = this.pagedTemplates.filter((item) => item.id !== id);
      if (this.pagedTemplates.length < before) {
        this.pageInfo = {
          ...this.pageInfo,
          totalCount: Math.max(0, this.pageInfo.totalCount - 1),
        };
      }
    });
  }
}
